import { API_BASE_URL, handleResponse, apiRequest, getAuthHeaders } from '@/lib/api-client';

export interface Story {
  id: string;
  title: string;
  passage: string;
  theme_id: string;
  theme_slug?: string | null;
  word_count: number;
  questions: Question[];
  created_at: string;
  story_data?: any; // Raw story data from backend
}

export interface Question {
  id: string;
  question_text?: string;  // Frontend format
  question?: string;        // Backend format
  options: string[] | Record<string, string>; // Can be array or object with A,B,C,D keys
  correct_answer?: string;  // Frontend format
  answer?: string;          // Backend format
  explanation?: string;     // Frontend format
  reason?: string;          // Backend format
  focus?: string;
}

export interface GenerateStoryRequest {
  theme_id: string;
  language_code?: string;
  async_generation?: boolean;
  for_student_id?: string;
}

export interface StoryGenerationJobResponse {
  job_id: number;
  status: string;
  message: string;
}

export interface JobStatus {
  id: number;
  job_type: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  submitted_at: string;
  started_at: string | null;
  completed_at: string | null;
  result_data: {
    status_summary?: string;
    execution_time_ms?: number;
    job_output?: {
      story_id: string;
      theme_id: string;
      title: string;
      language_code: string;
    };
    error_details?: {
      error_type: string;
      error_message: string;
    };
  } | null;
}

// Transform backend story format to frontend format
export function transformStory(rawStory: any): Story {
  const story: Story = {
    id: rawStory.id,
    title: rawStory.title,
    passage: rawStory.passage,
    theme_id: rawStory.theme_id,
    theme_slug: rawStory.theme_slug,
    word_count: rawStory.word_count,
    created_at: rawStory.created_at,
    questions: [],
  };

  // Extract questions from story_data if available
  if (rawStory.story_data && rawStory.story_data.questions) {
    story.questions = rawStory.story_data.questions.map((q: any) => {
      // Convert backend format to frontend format
      const options = Array.isArray(q.options)
        ? q.options
        : Object.values(q.options || {});

      return {
        id: q.id,
        question_text: q.question || q.question_text,
        options,
        correct_answer: q.answer || q.correct_answer,
        explanation: q.reason || q.explanation,
        focus: q.focus,
      };
    });
  }

  return story;
}

export const storiesApi = {
  async generateStory(data: GenerateStoryRequest): Promise<Story | StoryGenerationJobResponse> {
    const response = await apiRequest<any>(
      '/stories',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      'Failed to create story'
    );

    // If async generation, return job response directly
    if (data.async_generation && 'job_id' in response) {
      return response as StoryGenerationJobResponse;
    }

    // Otherwise transform and return story
    return transformStory(response);
  },

  async getStory(storyId: string): Promise<Story> {
    const response = await fetch(`${API_BASE_URL}/stories/${storyId}`, {
      headers: getAuthHeaders(),
    });

    const rawStory = await handleResponse<any>(response, 'Failed to fetch story');
    return transformStory(rawStory);
  },

  async getLibraryStories(): Promise<Story[]> {
    const response = await fetch(`${API_BASE_URL}/stories/library`, {
      headers: getAuthHeaders(),
    });

    return handleResponse<Story[]>(response, 'Failed to fetch library stories');
  },

  async getAllStories(limit: number = 100): Promise<Story[]> {
    const response = await fetch(`${API_BASE_URL}/stories?limit=${limit}`, {
      headers: getAuthHeaders(),
    });

    return handleResponse<Story[]>(response, 'Failed to fetch all stories');
  },

  async saveStoryReflection(
    storyId: string,
    sessionId: string,
    reflection: string
  ): Promise<{ story_id: string; session_id: string; reflection: string | null; updated_at: string }> {
    const response = await fetch(`${API_BASE_URL}/stories/${storyId}/wrap-up`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        reflection,
      }),
    });

    return handleResponse(response, 'Failed to save wrap-up reflection');
  },
};
